import { useSyncExternalStore } from 'react';
import type { Characteristic } from '../../schema';
import { getRenderersVersion, rendererFor, subscribeRenderers } from '../../api/renderers';
import { CustomRendererHost } from './CustomRendererHost';
import { CharacteristicShell } from './Shell';
import { SelectView } from './SelectView';
import { ToggleView } from './ToggleView';
import { ListImageView } from './ListImageView';
import { MultiQuantityView } from './MultiQuantityView';
import { TextView } from './TextView';
import { AreaView } from './AreaView';
import { ExpressionView } from './ExpressionView';
import { CombiView } from './CombiView';
import { MatrixValueView } from './MatrixValueView';
import { ProductAttributeView } from './ProductAttributeView';
import { WebserviceView } from './WebserviceView';
import { PackageView } from './PackageView';
import { FileUploadView } from './FileUploadView';
import { ChartView } from './ChartView';
import { ActionButtonView } from './ActionButtonView';
import { StaticView } from './StaticView';
import { CaptchaView } from './CaptchaView';
import { UnknownView } from './UnknownView';

interface CharacteristicViewProps {
    characteristic: Characteristic;
    locale: string | undefined;
    /** For a combining custom renderer: every Characteristic the widget serves, this one first. */
    members?: readonly Characteristic[];
}

/**
 * The custom renderer registered for a Characteristic's CSS classes, if any.
 * Re-renders when renderers are registered or removed after load.
 */
export function useCharacteristicRenderer(characteristic: Characteristic) {
    useSyncExternalStore(subscribeRenderers, getRenderersVersion, getRenderersVersion);
    return rendererFor(characteristic.cssClass);
}

/**
 * Discriminated dispatcher: picks the read-only view for each of the 18
 * known Characteristic types (+ Unknown fallback). Most views render
 * inside {@link CharacteristicShell} for consistent title/info chrome.
 * The two "self-contained" types — `savebutton` (ActionButton, its own
 * label IS the UI) and `captcha` (the widget already labels itself) —
 * render bare.
 *
 * `static` Characteristics intentionally still go through the Shell:
 * legacy storefront rendered them as a regular abstract option (title
 * as `<label>`, `info` as the HTML body, optional `default_value` as
 * extra body). Bypassing the Shell would drop both the title and the
 * `info` block — exactly the regression that hid "Persönliche Angaben"
 * headlines in the carport contact form.
 */
export function CharacteristicView({ characteristic, locale, members }: CharacteristicViewProps) {
    const custom = useCharacteristicRenderer(characteristic);
    if (characteristic.type === 'savebutton') {
        return <ActionButtonView characteristic={characteristic} locale={locale} />;
    }
    if (characteristic.type === 'captcha') {
        // Captcha widget carries its own label/state UI — adding an outer
        // title/shell on top would just say "Captcha" twice. Render bare.
        return <CaptchaView characteristic={characteristic} />;
    }
    if (custom) {
        const served = members ?? [characteristic];
        return (
            <CharacteristicShell
                characteristic={characteristic}
                locale={locale}
                combinedIds={served.length > 1 ? served.map((c) => c.id) : undefined}
                bare={custom.renderer.replace === 'all'}
            >
                <CustomRendererHost
                    key={custom.name}
                    name={custom.name}
                    renderer={custom.renderer}
                    members={served}
                    locale={locale}
                    fallback={renderBody(characteristic, locale)}
                />
            </CharacteristicShell>
        );
    }
    return (
        <CharacteristicShell characteristic={characteristic} locale={locale}>
            {renderBody(characteristic, locale)}
        </CharacteristicShell>
    );
}

function renderBody(c: Characteristic, locale: string | undefined) {
    switch (c.type) {
        case 'select':
        case 'multiselect':
            return <SelectView characteristic={c} locale={locale} />;
        case 'radiobuttons':
        case 'checkbox':
            return <ToggleView characteristic={c} locale={locale} />;
        case 'listimage':
            return <ListImageView characteristic={c} locale={locale} />;
        case 'multiquantity':
            return <MultiQuantityView characteristic={c} locale={locale} />;
        case 'text':
            return <TextView characteristic={c} locale={locale} />;
        case 'area':
            return <AreaView characteristic={c} locale={locale} />;
        case 'expression':
            return <ExpressionView characteristic={c} />;
        case 'combi':
            return <CombiView characteristic={c} />;
        case 'matrixvalue':
            return <MatrixValueView characteristic={c} />;
        case 'productattribute':
            return <ProductAttributeView characteristic={c} />;
        case 'webservice':
            return <WebserviceView characteristic={c} />;
        case 'package':
            return <PackageView characteristic={c} locale={locale} />;
        case 'fileupload':
            return <FileUploadView characteristic={c} />;
        case 'chart':
            return <ChartView characteristic={c} />;
        case 'savebutton':
            // Dispatched without shell-wrap above; this branch is dead code
            // but TypeScript wants the discriminated union exhaustive.
            return <ActionButtonView characteristic={c} locale={locale} />;
        case 'static':
            return <StaticView characteristic={c} locale={locale} />;
        case 'captcha':
            // Dispatched bare above; this branch keeps TS-exhaustiveness happy.
            return <CaptchaView characteristic={c} />;
        case 'unknown':
            return <UnknownView characteristic={c} />;
    }
}
